import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { PlusSquare, ArrowUpRight, Storefront } from "@phosphor-icons/react";
import { PageHeader } from "@/components/layout/AppShell";
import { AgentAvatar } from "@/components/agent/AgentCard";
import { api, fmtEur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, hint, id }) => (
  <div className="bg-[#080808] p-6" data-testid={`kpi-${id}`}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className="mt-3 font-mono text-3xl font-semibold text-white">{value}</p>
    {hint && <p className="mt-1 text-xs text-zinc-600">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { data: d } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  return (
    <div>
      <PageHeader
        eyebrow="Panel de control"
        title={`Hola, ${user?.name?.split(" ")[0] || ""}.`}
        subtitle="El estado de tu fábrica de agentes de un vistazo."
        actions={<Link to="/agents/new" className="btn-primary" data-testid="dashboard-create-agent-button"><PlusSquare size={18} /> Crear agente</Link>}
      />
      <div className="space-y-10 px-6 py-10 lg:px-12">
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi id="agents" label="Agentes" value={d?.agents_total ?? "—"} hint={`${d?.agents_published ?? 0} publicados`} />
          <Kpi id="sales" label="Ventas" value={d?.sales_total ?? "—"} hint="Total histórico" />
          <Kpi id="revenue" label="Ingresos netos" value={fmtEur(d?.revenue_net)} hint={`Bruto ${fmtEur(d?.revenue_gross)} · comisión ${Math.round((d?.platform_fee || 0) * 100)}%`} />
          <Kpi id="messages" label="Mensajes" value={d?.messages_total ?? "—"} hint="Respondidos por tus agentes" />
        </div>
        <div className="grid gap-px border border-white/10 bg-white/10 xl:grid-cols-3">
          <div className="bg-[#080808] p-6 xl:col-span-2">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm font-semibold">Ingresos · últimos 30 días</p>
              <Link to="/sales" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="dashboard-sales-link">Ver ventas <ArrowUpRight size={14} /></Link>
            </div>
            <div className="h-64" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d?.chart || []}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(8)} stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={40} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }} formatter={(v) => fmtEur(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-[#080808] p-6">
            <p className="mb-4 text-sm font-semibold">Top agentes</p>
            {d?.top_agents?.length ? (
              <div className="space-y-px">
                {d.top_agents.map((a) => (
                  <Link key={a.agent_id} to={`/agents/${a.agent_id}/edit`} className="flex items-center gap-3 p-2 hover:bg-white/5" data-testid={`top-agent-${a.agent_id}`} style={{ transition: "background-color 160ms" }}>
                    <AgentAvatar icon={a.icon} color={a.color} size={34} />
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{a.name}</p><p className="text-xs text-zinc-500">{a.sales_count || 0} ventas · {a.message_count || 0} msgs</p></div>
                    <span className={`h-1.5 w-1.5 ${a.published ? "bg-emerald-500" : "bg-zinc-600"}`} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex h-48 flex-col items-start justify-center gap-3">
                <p className="text-sm text-zinc-500">Aún no has creado agentes.</p>
                <Link to="/agents/new" className="btn-ghost !py-2" data-testid="dashboard-empty-create">Crear el primero</Link>
              </div>
            )}
          </div>
        </div>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {[
            ["/agents/new", "Diseñar con IA", "Describe tu idea y genera el agente completo en segundos.", PlusSquare],
            ["/skills", "Crear una skill", "Encapsula un proceso de tu negocio y reutilízalo.", ArrowUpRight],
            ["/marketplace", "Explorar marketplace", `Tienes ${d?.library_total ?? 0} agentes en tu biblioteca.`, Storefront],
          ].map(([to, t, s, I]) => (
            <Link key={to} to={to} className="group bg-[#080808] p-6 hover:bg-[#101010]" data-testid={`quick-${to.slice(1).replace("/", "-")}`} style={{ transition: "background-color 200ms" }}>
              <I size={22} className="text-blue-500" />
              <p className="mt-4 font-display text-lg font-bold">{t}</p>
              <p className="mt-1 text-sm text-zinc-500">{s}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
